<template>
  <transition name="modal">
    <div v-if="showModal" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <div class="modal-header">
          <h3>
            <i class="fas fa-file-invoice"></i>
            {{ $t("invoicesMisc.sendLink.title") }}
          </h3>

          <button class="btn-close" @click="close" :disabled="isProcessing">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Error general -->
          <div v-if="error" class="alert-error">
            <i class="fas fa-circle-exclamation"></i>
            <div>{{ error }}</div>
          </div>

          <!-- Caso: ya existe factura -->
          <div v-if="status409" class="warning-box">
            <i class="fas fa-triangle-exclamation"></i>
            <div>
              <strong>{{ $t("invoicesMisc.sendLink.already.title") }}</strong>
              <p class="mb-0" v-html="$t('invoicesMisc.sendLink.already.onlyOne')"></p>
              <p class="mb-0 warning-box__sub">
                {{ $t("invoicesMisc.sendLink.already.offerPdf") }}
              </p>

              <div class="warning-box__actions">
                <button
                  v-if="existingPdfUrl"
                  class="btn-secondary"
                  @click="openPdf"
                  :disabled="isProcessing"
                >
                  <i class="fas fa-file-pdf"></i>
                  {{ $t("invoicesMisc.sendLink.openPdf") }}
                </button>
                <button
                  v-if="existingInvoiceId"
                  class="btn-save"
                  @click="sendExistingInvoiceWhatsapp"
                  :disabled="isProcessing"
                >
                  <span v-if="isProcessing" class="btn-spinner"></span>
                  <i
                    v-else
                    class="fab fa-whatsapp"
                    style="color: #fff"
                  ></i>
                  {{ isProcessing ? $t("invoicesMisc.sendLink.sending") : $t("invoicesMisc.sendLink.sendWhatsapp") }}
                </button>
              </div>
            </div>
          </div>

          <!-- Confirmación -->
          <div v-else class="info-box">
            <i class="fas fa-paper-plane"></i>
            <div>
              <strong>{{ $t("invoicesMisc.sendLink.info.title") }}</strong>
              <p class="mb-0">
                {{ $t("invoicesMisc.sendLink.info.desc") }}
              </p>
            </div>
          </div>

          <div class="rule-box" v-html="$t('invoicesMisc.sendLink.rule')"></div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="close" :disabled="isProcessing">
            {{ $t("invoicesMisc.common.cancel") }}
          </button>

          <button
            v-if="!status409"
            class="btn-save"
            @click="confirmSend"
            :disabled="isProcessing || !reservaId"
          >
            <span v-if="isProcessing" class="btn-spinner"></span>
            <i v-else class="fas fa-check" style="color: #fff"></i>
            {{ isProcessing ? $t("invoicesMisc.sendLink.sending") : $t("invoicesMisc.sendLink.accept") }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from "axios";
import toastr from "toastr";

export default {
  name: "ConfirmSendInvoiceLinkModal",
  props: {
    // ✅ NO required para evitar el warning cuando aún no existe reservationData.id
    reservaId: { type: [Number, String, null], default: null },
  },
  data() {
    return {
      showModal: false,
      isProcessing: false,
      error: "",
      status409: false,
      existingPdfUrl: "",
      existingInvoiceId: null,
    };
  },
  methods: {
    open() {
      this.reset();
      this.showModal = true;
    },
    close() {
      if (this.isProcessing) return;
      this.showModal = false;
    },
    reset() {
      this.isProcessing = false;
      this.error = "";
      this.status409 = false;
      this.existingPdfUrl = "";
      this.existingInvoiceId = null;
    },

    openPdf() {
      if (this.existingPdfUrl) window.open(this.existingPdfUrl, "_blank");
    },

    async sendExistingInvoiceWhatsapp() {
      if (!this.existingInvoiceId) return;

      this.isProcessing = true;
      this.error = "";

      try {
        const { data } = await axios.post(
          `/business/invoices/${this.existingInvoiceId}/send-whatsapp`,
        );

        if (data?.success) {
          toastr.success(data.message || this.$t("invoicesMisc.sendLink.toast.invoiceSent"));
          this.showModal = false;
          this.$nextTick(() => this.reset());
          return;
        }

        this.error = data?.message || this.$t("invoicesMisc.sendLink.err.sendInvoiceFailed");
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          this.$t("invoicesMisc.sendLink.err.sendInvoiceFailed");
        this.error = msg;
        toastr.error(msg);
      } finally {
        this.isProcessing = false;
      }
    },

    async confirmSend() {
      if (!this.reservaId) {
        toastr.warning(this.$t("invoicesMisc.sendLink.warn.noReservation"));
        return;
      }

      this.error = "";
      this.status409 = false;
      this.existingPdfUrl = "";
      this.isProcessing = true;

      try {
        const { data } = await axios.post(
          `/reservas/${this.reservaId}/invoices/send-link`,
          { send: true },
        );

        if (data?.success) {
          toastr.success(this.$t("invoicesMisc.sendLink.toast.linkSent"));

          // ✅ CIERRA SIEMPRE AL ÉXITO
          this.showModal = false;

          // ✅ opcional: resetea el estado para que al abrir quede limpio
          // (lo hago en nextTick para no pelear con la transición)
          this.$nextTick(() => {
            this.reset();
          });

          return; // 👈 importante para no caer en el else
        }

        this.error = data?.message || this.$t("invoicesMisc.sendLink.err.sendLinkFailed");
      } catch (e) {
        const status = e?.response?.status;
        const msg = e?.response?.data?.message || this.$t("invoicesMisc.sendLink.err.sendLinkFailed");

        if (status === 409) {
          this.status409 = true;
          this.error = msg;
          this.existingPdfUrl = e?.response?.data?.pdf_url || "";
          this.existingInvoiceId = e?.response?.data?.invoice_id || null;
          toastr.warning(this.$t("invoicesMisc.sendLink.already.title"));
        } else {
          this.error = msg;
          toastr.error(msg);
        }
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* Misma estética que tu modal bonito */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 22px 26px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a1a1a;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      color: #5e72e4;
      font-size: 1.15rem;
    }
  }
}

.btn-close {
  background: #f3f4f6;
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    color: #333;
  }

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.modal-body {
  padding: 26px;
}

.info-box {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #eef2ff;
  border: 1px solid rgba(94, 114, 228, 0.25);
  border-radius: 12px;
  color: #1e3a8a;

  i {
    color: #5e72e4;
    font-size: 1.2rem;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 4px;
  }
  p {
    margin: 0;
    color: #334155;
  }
}

.warning-box {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  color: #7c2d12;

  i {
    color: #f97316;
    font-size: 1.25rem;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 4px;
  }
  p {
    margin: 0;
  }
}

.warning-box__sub {
  margin-top: 8px !important;
  color: #9a3412;
}

.warning-box__actions {
  margin-top: 16px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.alert-error {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #991b1b;
  margin-bottom: 14px;

  i {
    margin-top: 2px;
  }
}

.rule-box {
  margin-top: 14px;
  padding: 12px 14px;
  background: #fbfbfb;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  color: #374151;
  font-size: 0.92rem;
}

.modal-footer {
  padding: 18px 26px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  background: #fafafa;
}

.btn-cancel {
  background: white;
  border: 1.5px solid #e5e7eb;
  padding: 11px 18px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-save {
  background: #5e72e4;
  color: white;
  border: none;
  padding: 11px 18px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover:not(:disabled) {
    background: #4c63d2;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-secondary {
  background: white;
  border: 1.5px solid #e5e7eb;
  padding: 10px 14px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #374151;

  i {
    color: #ef4444;
  }

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transition igual al tuyo */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-active .modal-container {
  animation: modalSlideIn 0.3s ease;
}
.modal-leave-active .modal-container {
  animation: modalSlideOut 0.25s ease;
}
@keyframes modalSlideIn {
  from {
    transform: translateY(-20px) scale(0.98);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}
@keyframes modalSlideOut {
  from {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  to {
    transform: translateY(-20px) scale(0.98);
    opacity: 0;
  }
}
</style>
